import React from 'react';
import Button from '../common/Button';

/**
 * Component for managing VO2 data table entries
 * @param {Object} props - Component props
 * @param {Array} props.value - Array of VO2 data entries
 * @param {Function} props.onChange - Function to call when data changes
 * @param {boolean} props.disabled - Whether the component is disabled
 */
const VO2DataTable = ({ value = [], onChange, disabled = false }) => {

  // Ensure we have at least one row
  const tableData = value.length > 0 ? value : [{ 
    stage: 1,
    time: '',
    hr: '',
    incline: '',
    speed: '',
    vo2_ml: '',
    vo2_lkg: '',
    ve_vo2: '',
    calories_min: '',
    calories_hr: ''
  }];

  // Add a new row to the table
  const handleAddRow = () => {
    const newRow = {
      stage: tableData.length + 1,
      time: '',
      hr: '',
      incline: '',
      speed: '',
      vo2_ml: '',
      vo2_lkg: '',
      ve_vo2: '',
      calories_min: '',
      calories_hr: ''
    };
    onChange([...tableData, newRow]);
  };

  // Remove a row from the table
  const handleRemoveRow = (index) => {
    if (tableData.length <= 1) return; // Keep at least one row
    
    const newData = [...tableData];
    newData.splice(index, 1);
    
    // Update stage numbers
    newData.forEach((row, idx) => {
      row.stage = idx + 1;
    });
    
    onChange(newData);
  };

  // Update a specific field in a row
  const handleFieldChange = (index, field, value) => {
    const newData = [...tableData];
    newData[index] = {
      ...newData[index],
      [field]: value
    };
    onChange(newData);
  };

  return (
    <div className="mb-6">
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-3 border-b text-left">#</th>
              <th className="py-2 px-3 border-b text-left">Time (min:sec)</th>
              <th className="py-2 px-3 border-b text-left">HR (BPM)</th>
              <th className="py-2 px-3 border-b text-left">Incline (Grade)</th>
              <th className="py-2 px-3 border-b text-left">Speed (KPH)</th>
              <th className="py-2 px-3 border-b text-left">VO2 (mL/min)</th>
              <th className="py-2 px-3 border-b text-left">VO2 (L/kg/min)</th>
              <th className="py-2 px-3 border-b text-left">VE/VO2</th>
              <th className="py-2 px-3 border-b text-left">Calories (Cals/min)</th>
              <th className="py-2 px-3 border-b text-left">Calories (Cals/hr)</th>
              <th className="py-2 px-3 border-b text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    value={row.stage}
                    disabled={true}
                    className="w-16 p-1 border rounded"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="text"
                    value={row.time}
                    onChange={(e) => handleFieldChange(index, 'time', e.target.value)}
                    disabled={disabled}
                    placeholder="1:59"
                    className="w-20 p-1 border rounded"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    value={row.hr}
                    onChange={(e) => handleFieldChange(index, 'hr', e.target.value)}
                    disabled={disabled}
                    className="w-16 p-1 border rounded"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="0.1"
                    value={row.incline}
                    onChange={(e) => handleFieldChange(index, 'incline', e.target.value)}
                    disabled={disabled}
                    className="w-16 p-1 border rounded"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="0.1"
                    value={row.speed}
                    onChange={(e) => handleFieldChange(index, 'speed', e.target.value)}
                    disabled={disabled}
                    className="w-16 p-1 border rounded"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="1"
                    value={row.vo2_ml}
                    onChange={(e) => handleFieldChange(index, 'vo2_ml', e.target.value)}
                    disabled={disabled}
                    className="w-20 p-1 border rounded"
                    placeholder="653"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="0.01"
                    value={row.vo2_lkg}
                    onChange={(e) => handleFieldChange(index, 'vo2_lkg', e.target.value)}
                    disabled={disabled}
                    className="w-20 p-1 border rounded"
                    placeholder="10.2"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="0.01"
                    value={row.ve_vo2}
                    onChange={(e) => handleFieldChange(index, 've_vo2', e.target.value)}
                    disabled={disabled}
                    className="w-20 p-1 border rounded"
                    placeholder="24.42"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="0.1"
                    value={row.calories_min}
                    onChange={(e) => handleFieldChange(index, 'calories_min', e.target.value)}
                    disabled={disabled}
                    className="w-20 p-1 border rounded"
                    placeholder="3.3"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <input
                    type="number"
                    step="1"
                    value={row.calories_hr}
                    onChange={(e) => handleFieldChange(index, 'calories_hr', e.target.value)}
                    disabled={disabled}
                    className="w-20 p-1 border rounded"
                    placeholder="198"
                  />
                </td>
                <td className="py-2 px-3 border-b">
                  <button
                    onClick={() => handleRemoveRow(index)}
                    disabled={disabled || tableData.length <= 1}
                    className="text-red-500 hover:text-red-700 disabled:text-gray-400"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4">
        <Button
          variant="secondary"
          onClick={handleAddRow}
          disabled={disabled}
        >
          Add Row
        </Button>
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        <p>Note: Enter data for each stage of the VO2 Max test. The table will be displayed in the report.</p>
      </div>
    </div>
  );
};

export default VO2DataTable;
